import { keyBy, partition } from 'lodash'
import { envRefs } from '@wix/thunderbolt-catharsis'
import type {
	BaseDataItem,
	Component,
	PresetData,
	InnerElementsMap,
	ResolvedBuilderWidgetDataItem,
	ReducedManifest,
	BuilderTypeToPresetInfo,
} from '@wix/thunderbolt-becky-types'
import type { FeatureRefs } from '../cssFeatures.types'
import { createCssCompNode } from '../cssCompNode'
import { isRefArray, isVariantRelation } from '../../utils/variantsUtils'
import { isBreakpointRange } from '../../utils/is'
import type { BpToPresetsCss } from './presets.types'
import { getPresetsInstanceWrapperSelector } from '@wix/thunderbolt-commons'

// TODO: decide on data type
const isPresetDataItem = (presetVariant: BaseDataItem) => {
	if (presetVariant.type === 'PresetData') {
		return true
	}
	if (presetVariant.type.endsWith('.Presets')) {
		return true
	}

	return false
}

const getPresetData = (presetVariant: BaseDataItem) => {
	if (isPresetDataItem(presetVariant)) {
		return {
			presetName: (presetVariant as PresetData).presetName,
			variants: [],
		}
	}
	if (isVariantRelation(presetVariant)) {
		return {
			presetName: (presetVariant.to as PresetData).presetName,
			variants: presetVariant.variants,
		}
	}
}

const getPresetKeysFromReducedManifest = ({
	reducedManifest,
	path,
	presetName,
}: {
	reducedManifest: ReducedManifest
	path: string
	presetName: string
}): {
	presetVariableName: string
	customCssKey: string
} => {
	const { presetVariableKey, presets } = reducedManifest.elementData[path]

	return {
		presetVariableName: presetVariableKey ? `--preset-${presetVariableKey}` : '',
		customCssKey: presets?.[presetName]?.customCssKey || presetName,
	}
}

const generateInnerElements = (
	innerElementsDataItem: ResolvedBuilderWidgetDataItem,
	parentKey: string
): InnerElementsMap => {
	const result: InnerElementsMap = {}
	const innerElements = innerElementsDataItem?.innerElements

	if (!innerElements) {
		return result
	}

	for (const innerElementName in innerElements) {
		if (typeof innerElements[innerElementName] === 'object') {
			const combinedKey = `${parentKey}/${innerElementName}`

			if ('presetsQuery' in innerElements[innerElementName]) {
				result[combinedKey] = innerElements[innerElementName]
			}

			if ('innerElementsQuery' in innerElements[innerElementName]) {
				const nestedResult = generateInnerElements(
					innerElements[innerElementName].innerElementsQuery,
					combinedKey
				)
				Object.assign(result, nestedResult)
			}
		}
	}

	return result
}

const generatePresetsCssByBp = (
	elementPath: string,
	presetsData: BaseDataItem<any>,
	reducedManifest: ReducedManifest,
): BpToPresetsCss => {
	const presetsRefArray = isRefArray(presetsData) ? presetsData.values : [presetsData]
	const presetsAcc: BpToPresetsCss = {}

	return presetsRefArray.reduce((acc: BpToPresetsCss, preset) => {
		const presetData = getPresetData(preset)
		if (!presetData) {
			return acc
		}
		const { presetName, variants } = presetData
		const [[breakpointVariant], variantsWithoutBp] = partition(variants, (variant) => isBreakpointRange(variant))

		const { presetVariableName, customCssKey } = getPresetKeysFromReducedManifest({
			reducedManifest,
			path: elementPath,
			presetName: presetName as string,
		})

		const breakpointId = breakpointVariant?.id || 'default'
		acc[breakpointId] = acc[breakpointId] || []
		acc[breakpointId].push({
			presetCss: `${presetVariableName}: ${customCssKey}`,
			variants: keyBy(variantsWithoutBp, 'id'),
		})

		return acc
	}, presetsAcc)
}

const generateExternalPresetsCssByBp = ({
	presetsData,
	builderTypeToPresetInfo,
	builderType
}: {
	presetsData: BaseDataItem<any>
	builderTypeToPresetInfo: BuilderTypeToPresetInfo
	builderType: string
}) => {
	const presetsRefArray = isRefArray(presetsData) ? presetsData.values : [presetsData]
	const presetsAcc: BpToPresetsCss = {}

	return presetsRefArray.reduce((acc: BpToPresetsCss, preset) => {
		const presetData = getPresetData(preset)
		const widgetHashes = builderTypeToPresetInfo[builderType]
		if (!presetData || !widgetHashes) {
			return acc
		}
		const { presetName, variants } = presetData
		const [[breakpointVariant], variantsWithoutBp] = partition(variants, (variant) => isBreakpointRange(variant))

		const { presetVariableKey, presetHashes } = widgetHashes
		const presetVariableName = `--preset-${presetVariableKey}`
		const customCssKey = presetHashes?.[presetName]

		const breakpointId = breakpointVariant?.id || 'default'
		acc[breakpointId] = acc[breakpointId] || []
		acc[breakpointId].push({
			presetCss: `${presetVariableName}: ${customCssKey}`,
			variants: keyBy(variantsWithoutBp, 'id'),
		})

		return acc
	}, presetsAcc)
}

type RefParentResult = {
	pathFromRoot: string
	builderType: string
}

const getRefParent = (path: string, reducedManifest: ReducedManifest, rootBuilderType: string): RefParentResult => {
	const pathParts = path.split('/')

	// If we're at root level or one level deep, return root
	if (pathParts.length <= 2) {
		return { pathFromRoot: 'root', builderType: rootBuilderType }
	}

	const parentPath = pathParts.slice(0, -1).join('/')
	const elementData = reducedManifest.elementData[parentPath]

	// If parent is a refElement, return its type; otherwise recursively search up the tree
	if (elementData?.type) {
		return {
			pathFromRoot: parentPath,
			builderType: elementData.type as string
		}
	}

	return getRefParent(parentPath, reducedManifest, rootBuilderType)
}

type ParentSelectorItem = {
	builderType: string
	relativePath: string
}

const getArrayToBuildParentSelector = (
	path: string,
	reducedManifest: ReducedManifest,
	rootBuilderType: string
): ParentSelectorItem[] => {
	const pathHierarchy: string[] = []
	const pathToBuilderTypeMap = new Map<string, string>() // pathFromRoot -> builderType

	// Build hierarchy from child to root
	function buildHierarchy(currentPath: string): void {
		const refParent = getRefParent(currentPath, reducedManifest, rootBuilderType)
		pathToBuilderTypeMap.set(currentPath, refParent.builderType)
		pathHierarchy.push(currentPath)

		// Continue recursively until we reach root or find a path we've already processed
		if (refParent.pathFromRoot !== 'root' && !pathToBuilderTypeMap.has(refParent.pathFromRoot)) {
			buildHierarchy(refParent.pathFromRoot)
		}
	}

	buildHierarchy(path)

	// Build result array from root to child (reverse order), excluding the leaf
	const result: ParentSelectorItem[] = []
	for (let i = pathHierarchy.length - 1; i >= 0; i--) {
		const currentPath = pathHierarchy[i]
		const parentPath = i === 0 ? 'root' : pathHierarchy[i - 1]
		const relativePath = currentPath.replace(parentPath, 'root')
		const builderType = pathToBuilderTypeMap.get(currentPath)!

		result.push({ builderType, relativePath })
	}

	return result
}

const buildExternalParentSelector = ({
	pathParts,
	reducedManifest,
	builderTypeToPresetInfo,
	rootBuilderType
}: {
	pathParts: string[]
	reducedManifest: ReducedManifest
	builderTypeToPresetInfo: BuilderTypeToPresetInfo
	rootBuilderType: string
}) => {
	const parentPath = pathParts.slice(0, -1).join('/')
	const innerRefElements = getArrayToBuildParentSelector(parentPath, reducedManifest, rootBuilderType)

	const parentSelector = innerRefElements.reduce((acc, item) => {
		const innerSelector = builderTypeToPresetInfo?.[item.builderType]?.innerElementsSelectors?.[item.relativePath] || ''
		return `${acc} ${innerSelector}`
	}, reducedManifest.elementData.root.selector)

	return parentSelector
}

export const presets = createCssCompNode('presets', 'presetsOutput', {
	getDependencies: (component: Component, refs: FeatureRefs<'presets'>) => {
		if (!component.builderType) {
			return null
		}

		return {
			rootPresetsData: component.presetsQuery ? refs.presetsQuery(component.presetsQuery) : undefined,
			reducedManifest: envRefs.builderTypeToReducedManifest(component.builderType),
			innerElementsDataItem: component.innerElementsQuery
				? refs.innerElementsQuery(component.innerElementsQuery)
				: undefined,
			experiments: envRefs.experiments,
			builderTypeToPresetInfo: envRefs.builderTypeToPresetInfo(component.builderType),
		}
	},
	toViewItem: (component, deps) => {
		if (!component.builderType || !deps) {
			return null
		}
		const { rootPresetsData, innerElementsDataItem, reducedManifest, builderTypeToPresetInfo, experiments } = deps
		if (!reducedManifest) {
			return null
		}
		const supportExternalDependenciesCss = !!experiments['specs.thunderbolt.supportExternalDependenciesCss']
		if (supportExternalDependenciesCss && !builderTypeToPresetInfo) {
			return null
		}

		const selectorToPresets: { [selector: string]: BpToPresetsCss } = {}

		if (supportExternalDependenciesCss) {
			if (rootPresetsData) {
				selectorToPresets.root = generateExternalPresetsCssByBp({
					presetsData: rootPresetsData,
					builderTypeToPresetInfo,
					builderType: component.builderType
				})
			}
			if (innerElementsDataItem) {
				const innerElements = generateInnerElements(innerElementsDataItem, 'root')

				for (const innerElementPath in innerElements) {
					const pathParts = innerElementPath.split('/')

					const innerElementKey = pathParts[pathParts.length - 1]
					const innerElementWrapperSelector = getPresetsInstanceWrapperSelector(innerElementKey, true)
					const parentSelector = buildExternalParentSelector({
						pathParts,
						reducedManifest,
						builderTypeToPresetInfo,
						rootBuilderType: component.builderType
					})

					const innerElementFullSelector = `${parentSelector} ${innerElementWrapperSelector}`

					const innerPresetsData = innerElements[innerElementPath].presetsQuery as BaseDataItem<any>
					const innerElementBuilderType = reducedManifest.elementData[innerElementPath].type

					const innerPresetsByBp = innerElementBuilderType
						? generateExternalPresetsCssByBp({
							presetsData: innerPresetsData,
							builderTypeToPresetInfo,
							builderType: innerElementBuilderType
						})
						// here we still "support" inline presets selection - but serverless part is missing
						: generatePresetsCssByBp(innerElementPath, innerPresetsData, reducedManifest)

					selectorToPresets[innerElementFullSelector] = innerPresetsByBp
				}
			}
		} else {
			if (rootPresetsData) {
				selectorToPresets.root = generatePresetsCssByBp('root', rootPresetsData, reducedManifest)
			}
			if (innerElementsDataItem) {
				const innerElements = generateInnerElements(innerElementsDataItem, 'root')

				for (const innerElementPath in innerElements) {
					const innerElementFullSelector = reducedManifest.elementData[innerElementPath].selector
					const innerPresetsData = innerElements[innerElementPath].presetsQuery as BaseDataItem<any>
					const innerPresetsByBp = generatePresetsCssByBp(innerElementPath, innerPresetsData, reducedManifest)

					selectorToPresets[innerElementFullSelector] = innerPresetsByBp
				}
			}
		}

		return { presets: selectorToPresets, supportExternalDependenciesCss }
	},
})
